import { cancelQueryReads } from 'src/modules/shared/utils/api/cancel-query-reads.util';
import type { QueryClient } from '@tanstack/react-query';
import { conversationQueryOptions } from '../utils/api/conversation-query.util';
import {
  conversationRecoveryQueryKey,
  getConversationRecoveryMinimum,
} from '../utils/api/conversation-recovery-query.util';
import { recoverConversationDocuments } from './conversation-document-recovery.service';
import { updateFunnelConversationResourceCache } from '../utils/cache/funnel-conversations/update.util';
import { ApiError } from 'src/api/generated/core/ApiError';

/**
 * Obtains canonical authority when equal-time socket events cannot be ordered.
 * Cancels pre-notification reads, bounds the fresh request and retains polling
 * recovery on denial/failure. No socket arrival order is treated as a revision.
 * @param queryClient - Current account's cache owner.
 * @param conversationId - Authorized CRM conversation identity.
 * @param tenantId - Tenant that must own the returned resource.
 * @param minimumRevision - Canonical conversation revision to reach, when known.
 * Related Message deletions do not create a new conversation revision.
 * @returns Completion of the best-effort canonical read and search catch-up.
 */
export const recoverCanonicalConversation = async (
  queryClient: QueryClient,
  conversationId: number,
  tenantId: string,
  minimumRevision?: string
): Promise<void> => {
  const options = conversationQueryOptions(conversationId);
  // Operation-specific deadlines must never become the ordinary detail query's policy.
  // Nest the temporary key so accepted socket snapshots/account reset still cancel it.
  const recoveryKey = conversationRecoveryQueryKey(conversationId, tenantId);
  const minimumTimestamp = Math.max(
    Date.parse(minimumRevision ?? '') || 0,
    getConversationRecoveryMinimum(queryClient, conversationId, tenantId)
  );
  const deadline = Date.now() + 30_000;
  cancelQueryReads(queryClient, { queryKey: options.queryKey });
  try {
    const recovered = await queryClient.fetchQuery({
      ...options,
      queryKey: recoveryKey,
      meta: { minimumRevision: minimumTimestamp, deadline },
      gcTime: 0,
      staleTime: 0,
      retry: (failures, error) =>
        failures < 5 &&
        Date.now() < deadline &&
        !(error instanceof ApiError && [401, 403, 404].includes(error.status)),
      retryDelay: (attempt) =>
        Math.min(
          1_000 * 2 ** attempt,
          8_000,
          Math.max(0, deadline - Date.now())
        ),
      queryFn: async (context) => {
        if (Date.now() >= deadline) {
          throw new Error('Canonical recovery deadline exhausted');
        }
        const timeout = window.setTimeout(
          () => {
            void queryClient.cancelQueries(
              { queryKey: recoveryKey, exact: true },
              { revert: false }
            );
          },
          Math.min(5_000, deadline - Date.now())
        );
        try {
          const result = await options.queryFn(context);
          if (result.id !== conversationId || result.tenantId !== tenantId) {
            throw new Error('Canonical conversation identity mismatch');
          }
          if (
            minimumTimestamp > 0 &&
            !(Date.parse(result.updatedAt) >= minimumTimestamp)
          ) {
            throw new Error(
              'Canonical conversation has not reached the notification revision'
            );
          }
          return result;
        } finally {
          window.clearTimeout(timeout);
        }
      },
    });
    const conversation = queryClient.setQueryData<
      Awaited<ReturnType<typeof options.queryFn>>
    >(options.queryKey, (current) =>
      options.structuralSharing(current, recovered)
    );
    if (!conversation || conversation.tenantId !== tenantId) return;
    updateFunnelConversationResourceCache(
      queryClient,
      {
        params: { id: conversationId, data: conversation },
      },
      conversation
    );
    await recoverConversationDocuments(queryClient, conversation);
  } catch {
    // Existing visibility/reconnect/polling recovery remains available.
  }
};
